import { Badge, Card, LuIcon, Spinner } from "@overmap-ai/blocks"
import { VideoFile } from "@overmap-ai/forms"
import { cx } from "class-variance-authority"
import { Play } from "lucide-react"
import { type ComponentProps, memo } from "react"

import { modelIconMapping } from "../../mappings/icons"

interface VideoFileThumbnailProps extends Omit<ComponentProps<typeof Card>, "children"> {
	file: File | null
	errored: boolean
}

export const VideoFileThumbnail = memo((props: VideoFileThumbnailProps) => {
	const { ref, className, file, errored, ...rest } = props

	const renderContent = () => {
		if (errored) {
			return (
				<div className="flex size-full items-center justify-center">
					<LuIcon icon={modelIconMapping.alertCircle} color="var(--base-a11)" />
				</div>
			)
		}

		if (!file) {
			return (
				<div className="flex size-full items-center justify-center">
					<Spinner size="xs" accentColor="base" />
				</div>
			)
		}

		return (
			<>
				<VideoFile
					className="pointer-events-none block size-full max-w-full object-cover"
					file={file}
					controls={false}
					inert
					playsInline
					preload="metadata"
				/>
				<div
					className="pointer-events-none absolute inset-0 flex items-center justify-center"
					aria-hidden="true"
				>
					<Badge icon accentColor="base" size="xs" variant="solid" radius="full" className="shadow-sm">
						<LuIcon icon={Play} fill="currentColor" />
					</Badge>
				</div>
			</>
		)
	}

	return (
		<Card ref={ref} className={cx("p-0! relative size-12 shrink-0 grow-0 overflow-hidden", className)} {...rest}>
			{renderContent()}
		</Card>
	)
})
VideoFileThumbnail.displayName = "VideoFileThumbnail"
